import i18n from "@/lang";
import { ApiResponseStatusEnum } from "@/model/communication/ApiResponseStatusEnum";
import { GetCurrentUserCommand } from "@/model/communication/auth/GetCurrentUserCommand";
import { GetCurrentUserResData } from "@/model/communication/auth/GetCurrentUserResData";
import { AuthResponseEnum } from "@/model/communication/auth/Login/AuthResponseEnum";
import { AutoLoginCommand } from "@/model/communication/auth/Login/AutoLoginCommand";
import { LoginCommand } from "@/model/communication/auth/Login/LoginCommand";
import { LoginRequest } from "@/model/communication/auth/Login/LoginRequest";
import { LoginResponse } from "@/model/communication/auth/Login/LoginResponse";
import { LogoutCommand } from "@/model/communication/auth/LogoutCommand";
import router from "@/router";
import * as routes from "@/router/routes";
import { RootState } from "@/store";
import { FETCH_ALL_DEVICE_TREE } from "@/store/modules/DevicesModule/actions";
import { RawLocation } from "vue-router";
import { ActionTree } from "vuex";
import { ADD_ALERT } from "../AlertModule/mutations";
import { AlertModel, AlertModelType } from "../AlertModule/types";
import { GET_SERVER_CONFIGURATION, GET_SUPPORTED_MODELS } from "../ApplicationModule/actions";
import * as dashboard_a from "../DashboardModule/actions";
import * as license_a from "../LicenseModule/actions";
import * as a_polling from "../PollingModule/actions";
import { SET_AZURE_DISPLAY_NAME, SET_CURRENT_USER } from "./mutations";

// Azure AD Login
import { LocalStorageManager, LocalStorageRequests } from "@/plugins/LocalStorageManager";
import jwt_decode from "jwt-decode";
import { SET_TV_MODE } from "../ApplicationModule/mutations";
import { AuthenticationState } from "./state";

export const LOGIN = "LOGIN";
export const LOGOUT = "LOGOUT";
export const SET_AUTHENTICATED = "SET_AUTHENTICATED";
export const GET_CURRENT_USER = "GET_CURRENT_USER";
export const AUTO_LOGIN = "AUTO_LOGIN";

export interface VuexLoginRequest {
    loginRequest: LoginRequest;
    callbackURL: string;
}

export default {
    async [LOGIN]({ commit, dispatch, state }, payload: VuexLoginRequest) {
        const currState: AuthenticationState = state as AuthenticationState;

        // imposto loginLoading
        currState.loginLoading = true;

        try {
            // lancio la chiamata all'api per il login
            let res: LoginResponse = await new LoginCommand(payload.loginRequest).execute();

            // gestisco la risposta dell'api
            if (res.responseStatus == ApiResponseStatusEnum.Success) {
                // sono riuscito a contattare il server
                currState.authState = res.authResponse;
                currState.accountUnlockedAt = res.accountUnlockedAt;
                LocalStorageManager.Write(LocalStorageRequests.JwtToken, res.token);
                LocalStorageManager.Write(LocalStorageRequests.JwtRefreshToken, res.refreshToken);

                // credenziali corrette -> accesso garantito
                if (currState.authState == AuthResponseEnum.Authenticated) {
                    // lancio l'action per impostare lo stato autenticato
                    await dispatch(SET_AUTHENTICATED, true);
                    if (payload.callbackURL != null && payload.callbackURL != routes.Root) router.push(payload.callbackURL);

                    if (res.organizationLicensesCloseToExpiration)
                        commit(ADD_ALERT, new AlertModel(AlertModelType.Warning, "" + i18n.t("login.organizationLicensesCloseToExpiration"), null));
                }
            } else {
                // non sono riuscito a contattare il server
                currState.authState = AuthResponseEnum.ServerUnreachable;
            }
        } catch (exc) {
            console.log(exc);
            currState.authState = AuthResponseEnum.ServerUnreachable;
        }

        // rimuovo loginLoading
        currState.loginLoading = false;
    },

    /**
     * Set authenticated
     */
    async [SET_AUTHENTICATED]({ dispatch, getters }, authenticated: boolean) {
        console.log("SET_AUTHENTICATED start, set to -->", authenticated);
        if (authenticated) {
            await dispatch(license_a.FETCH_LICENSE);
            await dispatch(GET_CURRENT_USER);
            await dispatch(dashboard_a.FETCH_DASHBOARD_LIST);

            let promises = [];
            promises.push(dispatch(GET_SUPPORTED_MODELS));
            promises.push(dispatch(GET_SERVER_CONFIGURATION));
            if (getters.notificationEnabled) promises.push(dispatch(a_polling.NOTIFICATIONS_POLLING));
            promises.push(dispatch(a_polling.JWT_REFRESH));
            promises.push(dispatch(FETCH_ALL_DEVICE_TREE));
            await Promise.all(promises);
            if (router.currentRoute.path == routes.Login) {
                router.replace(routes.Root);
            }
        } else {
            if (router.currentRoute.path != routes.Login) {
                let location: RawLocation = {
                    path: routes.Login,
                };

                if (router.currentRoute.path != routes.Root) {
                    location.query = { callbackURL: router.currentRoute.path };
                    console.log("router.replace to", location);
                }

                router.replace(location);
            }

            if (this.state.application.startupData.isWSFedAuthenticationEnabled) {
                console.log("Try login as WS Fed");

                const callbackURL = router.currentRoute.query["callbackURL"] ? (router.currentRoute.query["callbackURL"] as string) : routes.Root;
                let loginRequest: LoginRequest = new LoginRequest(null, null, null, false, false, null, null, null, null, true);
                let req: VuexLoginRequest = { loginRequest: loginRequest, callbackURL: callbackURL };

                await dispatch(LOGIN, req);
            }
        }

        // state
        localStorage.setItem("authenticated", String(authenticated));
        console.log("SET_AUTHENTICATED finish, setted to -->", authenticated);
    },

    async [GET_CURRENT_USER]({ commit, dispatch, state }) {
        let res: GetCurrentUserResData = await new GetCurrentUserCommand().execute();

        if (res.responseStatus == ApiResponseStatusEnum.Success) {
            commit(SET_CURRENT_USER, res.currentUser);
            if (res.currentUser.name.includes("___guest")) commit(SET_TV_MODE, true);

            if (res.currentUser.isWindowsLogin) {
                // potrebbe essere Azure AD quindi provo a recuperare il display name
                let idToken = LocalStorageManager.Read(LocalStorageRequests.AzureAccessToken);
                if (idToken) commit(SET_AZURE_DISPLAY_NAME, jwt_decode(idToken)["name"]);
            }

            state.passwordValidDays = res.passwordValidDays;

            if (res.passwordValidDays > 0 && res.passwordValidDays < 14) {
                commit(ADD_ALERT, new AlertModel(AlertModelType.Warning, i18n.tc("login.pw_expiration", res.passwordValidDays), null));
            }
        }
    },

    async [AUTO_LOGIN]({ dispatch, state }) {
        const currState: AuthenticationState = state as AuthenticationState;
        try {
            let params = new URL(window.location.href).searchParams;
            if (params.has("token") && params.has("refreshtoken")) {
                LocalStorageManager.Write(LocalStorageRequests.JwtToken, params.get("token"));
                LocalStorageManager.Write(LocalStorageRequests.JwtRefreshToken, params.get("refreshtoken"));
            }
            let res = await new AutoLoginCommand().execute();

            // se sono già autenticato sul server
            if (res.responseStatus == ApiResponseStatusEnum.Success && res.authResponse == AuthResponseEnum.Authenticated) {
                await dispatch(SET_AUTHENTICATED, true);
                state.authState = AuthResponseEnum.Authenticated;
            } else if (res.responseStatus == ApiResponseStatusEnum.Error) throw "UnAuthorized";
            else await dispatch(SET_AUTHENTICATED, false);
        } catch (exc) {
            console.error("Error in AUTO_LOGIN action:", exc);
            if (exc == "PermissionDenied" || exc == "AuthenticationRequired" || exc == "UnAuthorized") {
                LocalStorageManager.Remove(LocalStorageRequests.JwtToken);
                LocalStorageManager.Remove(LocalStorageRequests.JwtRefreshToken);
                currState.authState = AuthResponseEnum.NotAuthenticated;
            } else currState.authState = AuthResponseEnum.ServerUnreachable;
            await dispatch(SET_AUTHENTICATED, false);
            if (!router.currentRoute.fullPath.includes(routes.Login)) router.push(routes.Login);
        }
    },

    /**
     * Logout Method
     */
    async [LOGOUT]({ dispatch }) {
        await new LogoutCommand().execute();
        LocalStorageManager.Remove(LocalStorageRequests.JwtToken);
        LocalStorageManager.Remove(LocalStorageRequests.JwtRefreshToken);
        dispatch(SET_AUTHENTICATED, false);
        router.push(routes.Login);
        location.reload();
    },
} as ActionTree<AuthenticationState, RootState>;
